import { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertCircle,
    CheckCircle2,
    Clock3,
    Eye,
    FileText,
    IndianRupee,
    Loader2,
    MoreHorizontal,
    Plus,
    ReceiptText,
    Send,
    Trash2,
    TrendingUp,
    UploadCloud,
    WalletCards,
    XCircle,
} from "lucide-react";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { MIN_TEXT_WORDS, getMinimumWordsError } from "../../utils/textValidation";

const emptyForm = {
    title: "",
    amount: "",
    date: "",
    description: "",
    receipt: "",
    receiptName: "",
};

const getExpenseCacheKey = (employee) => {
    const id = employee?._id || employee?.id;
    return id ? `employee_expense_claims_${id}` : null;
};

const readExpenseCache = (employee) => {
    const cacheKey = getExpenseCacheKey(employee);
    if (!cacheKey) return [];

    try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
        if (!cached?.data) return [];
        return Array.isArray(cached.data) ? cached.data : [];
    } catch {
        localStorage.removeItem(cacheKey);
        return [];
    }
};

const writeExpenseCache = (employee, data) => {
    const cacheKey = getExpenseCacheKey(employee);
    if (!cacheKey) return;
    localStorage.setItem(cacheKey, JSON.stringify({ data, savedAt: Date.now() }));
};

const getExpenseDraftKey = (employee) => {
    const id = employee?._id || employee?.id;
    return id ? `employee_expense_draft_${id}` : null;
};

const readExpenseDraft = (employee) => {
    const key = getExpenseDraftKey(employee);
    if (!key) return emptyForm;
    try {
        return { ...emptyForm, ...(JSON.parse(localStorage.getItem(key) || "null") || {}) };
    } catch {
        localStorage.removeItem(key);
        return emptyForm;
    }
};

const writeExpenseDraft = (employee, form) => {
    const key = getExpenseDraftKey(employee);
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(form));
};

const clearExpenseDraft = (employee) => {
    const key = getExpenseDraftKey(employee);
    if (key) localStorage.removeItem(key);
};

const readAnyExpenseCache = () => {
    try {
        const keys = Object.keys(localStorage).filter((key) => key.startsWith("employee_expense_claims_"));
        let latest = null;
        keys.forEach((key) => {
            const cached = JSON.parse(localStorage.getItem(key) || "null");
            if (!cached?.data || !Array.isArray(cached.data)) return;
            if (!latest || cached.savedAt > latest.savedAt) latest = cached;
        });
        return latest?.data || [];
    } catch {
        return [];
    }
};

const Expenses = () => {
    const { employee } = useAuth();
    const cachedClaims = useMemo(() => readExpenseCache(employee), [employee]);
    const [claims, setClaims] = useState(() => cachedClaims.length ? cachedClaims : readAnyExpenseCache());
    const [modalOpen, setModalOpen] = useState(false);
    const [openClaimId, setOpenClaimId] = useState("");
    const [form, setForm] = useState(() => readExpenseDraft(employee));
    const [message, setMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (cachedClaims.length) setClaims(cachedClaims);
    }, [cachedClaims]);

    useEffect(() => {
        const hasDraft = Object.values(form).some(Boolean);
        if (hasDraft) writeExpenseDraft(employee, form);
    }, [employee, form]);

    const fetchClaims = useCallback(async () => {
        try {
            const res = await api.get("/api/employee/expenses/my", {
                params: { limit: 8 },
            });
            const nextClaims = Array.isArray(res.data) ? res.data : [];
            setClaims(nextClaims);
            writeExpenseCache(employee, nextClaims);
        } catch (err) {
            console.error("Failed to load expense claims:", err);
        }
    }, [employee]);

    useEffect(() => {
        fetchClaims();
    }, [fetchClaims]);

    const totals = useMemo(() => {
        return claims.reduce(
            (acc, claim) => {
                const amount = Number(claim.amount || 0);
                if (claim.status === "Approved") acc.approved += amount;
                if (claim.status === "Pending") acc.pending += amount;
                if (claim.status === "Rejected") acc.rejectedAmount += amount;
                if (claim.status === "Rejected") acc.rejected += 1;
                acc.total += amount;
                acc.counts[claim.status] = (acc.counts[claim.status] || 0) + 1;
                return acc;
            },
            { approved: 0, pending: 0, rejected: 0, rejectedAmount: 0, total: 0, counts: {} },
        );
    }, [claims]);

    const categoryBreakdown = useMemo(() => {
        const buckets = claims.reduce((acc, claim) => {
            const key = claim.category || claim.expenseCategory || "General";
            acc[key] = (acc[key] || 0) + Number(claim.amount || 0);
            return acc;
        }, {});
        return Object.entries(buckets)
            .map(([label, amount]) => ({
                label,
                amount,
                percent: totals.total ? Math.round((amount / totals.total) * 100) : 0,
            }))
            .sort((a, b) => b.amount - a.amount)
            .slice(0, 5);
    }, [claims, totals.total]);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.type !== "application/pdf") {
            setMessage("Only PDF receipt files are supported.");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setMessage("Receipt file must be less than 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setForm((current) => ({
                ...current,
                receipt: reader.result,
                receiptName: file.name,
            }));
            setMessage("");
        };
        reader.readAsDataURL(file);
    };

    const removeReceipt = () => {
        setForm((current) => ({
            ...current,
            receipt: "",
            receiptName: "",
        }));
    };

    const closeModal = () => {
        if (submitting) return;
        setModalOpen(false);
        setMessage("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage("");

        const descriptionError = getMinimumWordsError(form.description, "Expense description");
        if (descriptionError) {
            setMessage(descriptionError);
            return;
        }

        if (!form.receipt || !form.receiptName) {
            setMessage("Receipt PDF is required for every expense claim.");
            return;
        }

        if (!String(form.receipt).startsWith("data:application/pdf") || !String(form.receiptName).toLowerCase().endsWith(".pdf")) {
            setMessage("Only PDF receipt files are allowed.");
            return;
        }

        const optimisticId = `pending-${Date.now()}`;
        const optimisticClaim = {
            _id: optimisticId,
            title: form.title,
            amount: Number(form.amount || 0),
            date: form.date,
            description: form.description,
            receiptName: form.receiptName,
            status: "Pending",
            createdAt: new Date().toISOString(),
        };
        const previousClaims = claims;

        setClaims((current) => {
            const nextClaims = [optimisticClaim, ...current].slice(0, 8);
            writeExpenseCache(employee, nextClaims);
            return nextClaims;
        });
        setSubmitting(true);

        try {
            const res = await api.post("/api/employee/expenses/apply", form);
            setClaims((current) => {
                const nextClaims = current.map((claim) => claim._id === optimisticId ? res.data : claim);
                writeExpenseCache(employee, nextClaims);
                return nextClaims;
            });
            setForm(emptyForm);
            clearExpenseDraft(employee);
            setModalOpen(false);
            window.setTimeout(fetchClaims, 0);
        } catch (err) {
            setClaims(previousClaims);
            writeExpenseCache(employee, previousClaims);
            setMessage(err.response?.data?.message || "Failed to submit expense claim.");
        } finally {
            setSubmitting(false);
        }
    };

    const viewPdf = (base64Data) => {
        const win = window.open();
        if (!win) {
            setMessage("Please allow popups to view the receipt.");
            return;
        }
        win.document.write(
            `<iframe src="${base64Data}" frameborder="0" style="border:0;position:fixed;inset:0;width:100%;height:100%;" allowfullscreen></iframe>`,
        );
    };

    const handleViewReceipt = async (claimId) => {
        try {
            const res = await api.get(`/api/expenses/${claimId}/receipt`);
            if (res.data?.receipt) {
                viewPdf(res.data.receipt);
            } else {
                setMessage("Receipt content not found.");
            }
        } catch (err) {
            setMessage("Failed to load receipt.");
        }
    };

  return (
        <div className="expenses-premium-wrapper expenses-ref-page text-[var(--text-primary)]">
            <main className="expenses-ref-shell">
                <div className="expenses-ref-layout">
                    <div className="expenses-ref-main">
                        <section className="expenses-premium-hero expenses-ref-hero">
                            <div className="expenses-hero-content">
                                <span className="premium-eyebrow text-[#7c3aed]">Expense Control</span>
                                <h1>Claims tracker</h1>
                                <p>Track, manage and get your expenses reimbursed seamlessly.</p>
                                <div className="premium-meta-row mt-4">
                                    <span>{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })}</span>
                                    <span>{claims.length} total claims</span>
                                </div>
                            </div>
                            <div className="expenses-hero-wallet" aria-hidden="true">
                                <WalletCards size={58} />
                            </div>
                            <div className="expenses-hero-total">
                                <span>Total Reimbursed</span>
                                <strong>{formatInr(totals.approved)}</strong>
                                <small><TrendingUp size={13} /> Approved claims</small>
                            </div>
                        </section>

                        <section className="expenses-ref-kpis">
                            <ExpenseStat label="Total Claims" value={claims.length} subLabel="All time" icon={<ReceiptText size={20} />} />
                            <ExpenseStat label="Pending" value={totals.counts.Pending || 0} subLabel={formatInr(totals.pending)} icon={<Clock3 size={20} />} tone="pending" />
                            <ExpenseStat label="Approved" value={totals.counts.Approved || 0} subLabel={formatInr(totals.approved)} icon={<CheckCircle2 size={20} />} tone="approved" />
                            <ExpenseStat label="Rejected" value={totals.rejected} subLabel={formatInr(totals.rejectedAmount)} icon={<XCircle size={20} />} tone="rejected" />
                        </section>

                        <section className="expenses-premium-board expenses-submit-board">
                            <div className="expenses-board-header">
                                <div>
                                    <h2>Submit and track</h2>
                                    <p>Submit new claims and track their status</p>
                                </div>
                                <button className="expenses-new-btn" type="button" onClick={() => setModalOpen(true)}>
                                    <Plus size={17} />
                                    {Object.values(form).some(Boolean) ? "Resume Draft" : "New Claim"}
                                </button>
                            </div>

                            <div className="expenses-upload-preview">
                                <button type="button" className="expenses-drop-zone" onClick={() => setModalOpen(true)}>
                                    <UploadCloud size={36} />
                                    <strong>Drag & drop bills here</strong>
                                    <span>or click to upload PDF receipts</span>
                                </button>
                            </div>
                        </section>

                        <section className="expenses-premium-board expenses-history-board">
                            <div className="expenses-board-header">
                                <div>
                                    <h2>Expense history</h2>
                                    <p>All your submitted claims</p>
                                </div>
                                <span className="expenses-count-pill">{claims.length} records</span>
                            </div>

                            {claims.length === 0 ? (
                                <div className="expenses-empty-ref">
                                    <IndianRupee size={34} />
                                    <strong>No expense claims yet</strong>
                                    <span>Submit your first claim using the New Claim button.</span>
                                </div>
                            ) : (
                                <div className="expenses-table-ref">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Description</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {claims.map((claim) => (
                                                <ExpenseRow
                                                    key={claim._id}
                                                    claim={claim}
                                                    open={openClaimId === claim._id}
                                                    onToggle={() => setOpenClaimId((current) => (current === claim._id ? "" : claim._id))}
                                                    onViewReceipt={() => handleViewReceipt(claim._id)}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>

                    <aside className="expenses-ref-side">
                        <section className="expenses-premium-board expenses-side-card">
                            <div className="expenses-side-title">
                                <h2>Claim Summary</h2>
                                <MoreHorizontal size={18} />
                            </div>
                            <div className="expenses-summary-grid">
                                <div className="expenses-donut-inner">
                                    <strong>{claims.length}</strong>
                                    <span>Total Claims</span>
                                </div>
                                <div className="expenses-summary-list">
                                    <SummaryLine label="Approved" value={totals.counts.Approved || 0} tone="approved" />
                                    <SummaryLine label="Pending" value={totals.counts.Pending || 0} tone="pending" />
                                    <SummaryLine label="Rejected" value={totals.rejected} tone="rejected" />
                                </div>
                            </div>
                            <div className="expenses-side-total">
                                <span>Total Amount</span>
                                <strong>{formatInr(totals.total)}</strong>
                            </div>
                        </section>

                        <section className="expenses-premium-board expenses-side-card">
                            <div className="expenses-side-title">
                                <h2>Expense by Category</h2>
                                <span>This Month</span>
                            </div>
                            <div className="expenses-category-list">
                                {categoryBreakdown.length ? categoryBreakdown.map((item) => (
                                    <div className="expenses-category-row" key={item.label}>
                                        <span>{item.label}</span>
                                        <i><em style={{ width: `${item.percent}%` }} /></i>
                                        <strong>{formatInr(item.amount)} ({item.percent}%)</strong>
                                    </div>
                                )) : <p className="expenses-side-empty">No category data yet.</p>}
                            </div>
                        </section>

                        <section className="expenses-premium-board expenses-side-card">
                            <div className="expenses-side-title">
                                <h2>Reimbursement ETA</h2>
                                <Clock3 size={18} />
                            </div>
                            <div className="expenses-eta-card">
                                <strong>{totals.counts.Pending || 0} pending</strong>
                                <span>Waiting for approval</span>
                            </div>
                        </section>
                    </aside>
                </div>

                {modalOpen && (
                    <div className="expense-modal-backdrop" onMouseDown={closeModal}>
                        <section className="expense-modal" onMouseDown={(event) => event.stopPropagation()}>
                            <header className="expense-modal-head">
                                <div>
                                    <span>New expense</span>
                                    <h2>Submit claim</h2>
                                </div>
                                <button type="button" onClick={closeModal} aria-label="Close expense form">
                                    <XCircle size={20} />
                                </button>
                            </header>

                            <form className="expense-form" onSubmit={handleSubmit}>
                                <div className="expense-form-grid">
                                    <ExpenseInput label="Purpose">
                                        <input
                                            type="text"
                                            required
                                            placeholder="Travel, client lunch, supplies..."
                                            value={form.title}
                                            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                                        />
                                    </ExpenseInput>
                                    <ExpenseInput label="Amount">
                                        <input
                                            type="number"
                                            required
                                            min="1"
                                            placeholder="₹"
                                            value={form.amount}
                                            onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
                                        />
                                    </ExpenseInput>
                                    <ExpenseInput label="Expense date">
                                        <input
                                            type="date"
                                            required
                                            max={new Date().toISOString().split("T")[0]}
                                            value={form.date}
                                            onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
                                        />
                                    </ExpenseInput>
                                </div>

                                <ExpenseInput label="Receipt PDF">
                                    {!form.receipt ? (
                                        <label className="expense-upload">
                                            <input type="file" accept="application/pdf" onChange={handleFileChange} />
                                            <FileText size={28} />
                                            <strong>Upload receipt</strong>
                                            <span>PDF only, max 5MB</span>
                                        </label>
                                    ) : (
                                        <div className="expense-file-row">
                                            <FileText size={22} />
                                            <div>
                                                <strong>{form.receiptName}</strong>
                                                <span>PDF receipt attached</span>
                                            </div>
                                            <button type="button" onClick={() => viewPdf(form.receipt)} aria-label="View receipt">
                                                <Eye size={17} />
                                            </button>
                                            <button type="button" onClick={removeReceipt} aria-label="Remove receipt">
                                                <Trash2 size={17} />
                                            </button>
                                        </div>
                                    )}
                                </ExpenseInput>

                                <ExpenseInput label="Description">
                                    <textarea
                                        rows={4}
                                        placeholder={`Add a note for approver in at least ${MIN_TEXT_WORDS} words...`}
                                        value={form.description}
                                        onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                                    />
                                </ExpenseInput>

                                {message && (
                                    <div className="expense-alert">
                                        <AlertCircle size={17} />
                                        {message}
                                    </div>
                                )}

                                <div className="expense-submit">
                                    <span>{form.amount ? formatInr(Number(form.amount)) : "Draft autosaved"}</span>
                                    <div className="expense-submit-actions">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setForm(emptyForm);
                                                clearExpenseDraft(employee);
                                                setMessage("");
                                            }}
                                            disabled={submitting}
                                        >
                                            Clear Draft
                                        </button>
                                        <button type="button" onClick={closeModal} disabled={submitting}>
                                            Cancel
                                        </button>
                                        <button type="submit" disabled={submitting}>
                                            {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                                            {submitting ? "Submitting" : "Submit Claim"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </section>
                    </div>
                )}
            </main>
        </div>
    );
};

const formatInr = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const ExpenseStat = ({ label, value, subLabel, icon, tone = "default" }) => (
    <div className={`expenses-kpi-glass ${tone}`}>
        <span>{icon}</span>
        <div>
            <small>{label}</small>
            <strong>{value}</strong>
            {subLabel && <em>{subLabel}</em>}
        </div>
    </div>
);

const SummaryLine = ({ label, value, tone }) => (
    <div className={`expenses-summary-line ${tone}`}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const ExpenseInput = ({ label, children }) => (
    <label className="expense-input">
        <span>{label}</span>
        {children}
    </label>
);

const ExpenseRow = ({ claim, open, onToggle, onViewReceipt }) => {
    const StatusIcon = claim.status === "Approved" ? CheckCircle2 : claim.status === "Rejected" ? XCircle : Clock3;

    return (
        <>
            <tr className={open ? "open" : ""}>
                <td>{formatDate(claim.date)}</td>
                <td>
                    <button type="button" className="expenses-table-title" onClick={onToggle}>
                        <strong>{claim.title}</strong>
                        <span>{claim.description || "No description"}</span>
                    </button>
                </td>
                <td>{formatInr(claim.amount)}</td>
                <td>
                    <small className={`expenses-status-pill ${claim.status}`}>
                        <StatusIcon size={12} />
                        {claim.status}
                    </small>
                </td>
                <td>
                    <button type="button" className="expenses-row-action" onClick={onToggle}>
                        <MoreHorizontal size={16} />
                    </button>
                </td>
            </tr>
            {open && (
                <tr className="expenses-table-detail">
                    <td colSpan={5}>
                        {claim.rejectionReason && <em>Rejection note: {claim.rejectionReason}</em>}
                        {claim.receiptName && (
                            <button type="button" onClick={onViewReceipt}>
                                <Eye size={15} />
                                View Receipt: {claim.receiptName}
                            </button>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
};

export default Expenses;
